import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";

const Cart = () => {
  const { items, updateQty, removeItem } = useCart();

  if (items.length === 0)
    return (
      <div data-testid="cart-empty" className="px-5 md:px-10 py-32 text-center min-h-[60vh]">
        <h1 className="font-serif text-5xl uppercase tracking-tighter mb-6">Your Bag is Empty</h1>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-10">
          The road awaits.
        </p>
        <Link
          to="/shop"
          data-testid="empty-cart-shop-button"
          className="inline-block bg-foreground text-background px-10 py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors"
        >
          Enter the Collection
        </Link>
      </div>
    );

  return (
    <div data-testid="cart-page" className="px-5 md:px-10 py-14 min-h-screen">
      <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none border-b border-border pb-8 mb-10">
        The Bag
      </h1>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 divide-y divide-border">
          {items.map((item) => (
            <div
              key={`${item.slug}-${item.size}`}
              data-testid={`cart-item-${item.slug}-${item.size}`}
              className="flex gap-5 py-6"
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-24 h-32 object-cover grayscale bg-secondary"
              />
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between">
                  <div>
                    <Link to={`/product/${item.slug}`} className="font-serif text-2xl uppercase">
                      {item.name}
                    </Link>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">
                      Size {item.size}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center border border-border">
                    <button
                      data-testid={`decrease-${item.slug}-${item.size}`}
                      onClick={() => updateQty(item.slug, item.size, item.quantity - 1)}
                      className="p-2 hover:bg-secondary"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-mono text-xs">{item.quantity}</span>
                    <button
                      data-testid={`increase-${item.slug}-${item.size}`}
                      onClick={() => updateQty(item.slug, item.size, item.quantity + 1)}
                      className="p-2 hover:bg-secondary"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    data-testid={`remove-${item.slug}-${item.size}`}
                    onClick={() => removeItem(item.slug, item.size)}
                    className="text-muted-foreground hover:text-[#5B2A86] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="border border-border p-6 sticky top-24">
            <h2 className="font-serif text-2xl uppercase mb-6">Summary</h2>
            <div className="space-y-3 font-mono text-sm border-b border-border pb-4">
              <div className="flex justify-between text-muted-foreground">
                <span>Pieces</span>
                <span data-testid="cart-count-summary">
                  {items.reduce((s, i) => s + i.quantity, 0)}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Pricing</span>
                <span>Enquire at checkout</span>
              </div>
            </div>
            <Link
              to="/checkout"
              data-testid="checkout-button"
              className="mt-6 w-full flex items-center justify-center gap-3 bg-foreground text-background py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors"
            >
              Reserve Selection <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
